// 261005 E1-P2 — thân đọc kịch bản MỘT tập, chép từ `ScriptViewModal` (E1-P1) bỏ vỏ Modal.
// Tên nhân vật / bối cảnh tra từ `outline` (tên ĐÃ bản địa hoá — bug 09/09), không đổi hành vi.
import React from 'react';
import { Alert, Collapse, Space, Typography } from 'antd';
import Button from './WbButton';
import type { EpisodeScriptRecord, FilmOutline, ScenePlanOrigin, ScriptDeliveryMode } from '../../../types/adaptation';
import { dedupeViolations, findScenePlanItem, gateLabel, humanizeCodes, scenePlanGateLabel, splitIssuesByKind } from '../adaptationUtils';
import OtherIssuesBlock, { OtherIssueRow } from './OtherIssuesBlock';
import { Bang } from './WbSlot';
import { characterColorStyle } from './characterColors';
import { isManualScene } from './scriptEditModel';
import { foldCriticDefects } from './episodeRowView';
import OptionsButton from './assistant/OptionsButton';
import { scriptRowOptionsRequest, scriptViolationChipIds } from './assistant/optionsAction';
import type { AssistantRequest } from './assistant/assistantIntent';
import PlantAcceptModal, { PlantAcceptTarget } from './PlantAcceptModal';
import { introBeatLabel } from './introLineView';
import DroppedSourceEventsBlock from './DroppedSourceEventsBlock';
import { sceneAnchorId } from './reviewBookView';
import { acceptedPlantLine, acceptedPlantRows, canOfferPlantAccept, plantAcceptTargets } from './plantAcceptView';
import { EPISODE_FOLDABLE_GATES, splitSoftHard } from './warningGroupView';
import { softWarningKindCount, warningOptionsRequestOf } from './scriptReaderWarningView';

const { Text, Paragraph } = Typography;

const ORIGIN_LABELS: Record<ScenePlanOrigin, string> = {
  SOURCE_DIRECT: 'Trực tiếp từ nguồn',
  SOURCE_DERIVED: 'Suy ra từ nguồn',
  ADAPTATION_ADDED: 'Thêm khi chuyển thể',
};

const DELIVERY_LABELS: Record<ScriptDeliveryMode, string> = {
  VISUAL: 'Hình ảnh', DIALOGUE: 'Thoại', VOICE_OVER: 'V.O.', MIXED: 'Phối hợp',
};

/** Nhảy tới cảnh: cuộn TRONG vùng giữa (`.wb-scroll`) chứ không cuộn cả trang; chừa chỗ cho thanh QuickNav dính. */
const NAV_OFFSET = 52;
export const scrollToAnchor = (id: string) => {
  const el = document.getElementById(id);
  if (!el) return;
  const box = el.closest('.wb-scroll') as HTMLElement | null;
  if (!box) { el.scrollIntoView({ behavior: 'smooth', block: 'start' }); return; }
  const top = el.getBoundingClientRect().top - box.getBoundingClientRect().top + box.scrollTop - NAV_OFFSET;
  box.scrollTo({ top, behavior: 'smooth' });
};

/** Thanh nhảy cảnh. */
const QuickNav: React.FC<{ items: { id: string; label: string }[] }> = ({ items }) => {
  const [active, setActive] = React.useState(0);
  return (
    <div className="wb-quicknav" role="tablist" aria-label="Nhảy tới cảnh">
      {items.map((item, i) => (
        <button key={item.id} type="button" role="tab" aria-selected={active === i} className={`wb-tab${active === i ? ' wb-tab-on' : ''}`} onClick={() => { setActive(i); scrollToAnchor(item.id); }}>{item.label}</button>
      ))}
    </div>
  );
};

interface Props {
  script: EpisodeScriptRecord;
  outline: FilmOutline | null;
  /** 261005 E3-P4b2/F67 — tập đang đọc + "Phương án (tốn AI)" ở mỗi vi phạm, kể cả CRITIC. Không `onAskOptions` ⇒ không nút. */
  episode?: number;
  onAskOptions?: (req: AssistantRequest) => void;
  /** Có ⇒ nút tắt kèm lý do (tập khoá / đang sửa / không nháp). */
  optionsDisabledReason?: string;
  /** 261015 P2a — nút $0 "Khôi phục thông tin cảnh" ở dòng `SCENE_METADATA_LOST` (không dùng AI). Thiếu ⇒ không nút. */
  onRestoreMetadata?: () => void;
  restoringMetadata?: boolean;
  /** 261015 P2b-3 — "Chấp nhận (cảnh đã có chi tiết)…" ở dòng chip `PLANT_NOT_WRITTEN` (có lý do; tạo khối xem thử $0). Thiếu ⇒ không nút. */
  onAcceptPlant?: (beatId: string, reason: string) => void | Promise<void>;
  acceptingPlant?: boolean;
  /** 261023 E3-P3a — mở sẵn khối «Tình tiết nguyên tác chưa lên phim» (đến từ ô «Chưa lên phim» của Sổ duyệt). */
  openDropped?: boolean;
  /** 3k-c lượt 4 — tập còn lỗi giám khảo: các cảnh báo `CRITIC_FINDING` (tiếng Anh, theo cảnh, kèm «Phương án») đã gập vào «Xem chi tiết» của trang tập ⇒ không vẽ ở khối «Cảnh báo của kịch bản». */
  hideCriticFindings?: boolean;
}

/** Id neo của một cảnh: theo `scenePlanId` (Sổ duyệt cuộn tới đúng cảnh); không có id ⇒ theo vị trí. QuickNav dùng cùng id. */
const sceneDomId = (sc: { scenePlanId?: string }, i: number): string => (sc?.scenePlanId ? sceneAnchorId(sc.scenePlanId) : `reader-scene-${i}`);

const ScriptReader: React.FC<Props> = ({ script: rec, outline, episode, onAskOptions, optionsDisabledReason, onRestoreMetadata, restoringMetadata, onAcceptPlant, acceptingPlant, openDropped, hideCriticFindings }) => {
  const [plantTarget, setPlantTarget] = React.useState<PlantAcceptTarget | null>(null);
  const charName = (id?: string) => {
    const c = outline?.characters.find((x) => x.id === id);
    return (c?.localizedName?.trim() || c?.name) ?? id ?? '';
  };
  const locName = (id: string) => {
    const l = outline?.locations.find((x) => x.id === id);
    return (l?.localizedName?.trim() || l?.name) ?? id;
  };
  const doc = rec.script;
  if (!doc) return null;
  // 261015 P1 (A1): id chip song song với danh sách ĐÃ GỘP ⇒ ghép id vào từng phần tử TRƯỚC khi tách lỗi / không-phải-lỗi (tách trước rồi tra `chipIds[index]` sẽ lệch).
  const chipIds = scriptViolationChipIds(rec.violations);
  const merged = dedupeViolations(rec.violations ?? []).map((v, i) => ({ ...v, chipId: chipIds[i] ?? null }));
  const { defects: allDefects, others } = splitIssuesByKind(merged);
  const defects = foldCriticDefects(allDefects, !!hideCriticFindings);
  const { soft: softDefects, hard: hardDefects } = splitSoftHard(defects, EPISODE_FOLDABLE_GATES);
  // F67 giữ: dòng điểm CRITIC nằm ở khối gập nhưng vẫn có nút "Phương án" (`scriptRowOptionsRequest` quyết hàng nào có nút; hàng khác không).
  const otherRows: OtherIssueRow[] = (['score', 'notice', 'unchecked', 'stale'] as const).flatMap((kind) => others[kind].map((v) => {
    const req = onAskOptions ? scriptRowOptionsRequest(episode, v, v.chipId) : null;
    return {
      gate: v.gate, message: v.message, kind,
      action: req && onAskOptions ? <OptionsButton disabledReason={optionsDisabledReason} onClick={() => onAskOptions(req)} /> : undefined,
    };
  }));
  // 261015 P2b-3: nhóm "Đã chấp nhận" của ⑤ (gieo) — lý do · người · lúc; BE tách khỏi dải lỗi (kind 'accepted'), không chip, không nút.
  for (const row of acceptedPlantRows(others.accepted)) {
    otherRows.push({ gate: 'PLANT_NOT_WRITTEN', kind: 'accepted', plain: true, message: acceptedPlantLine(row) });
  }
  return (
      <div className="wb-script-body">
        {doc.scenes.length > 1 && (
          <QuickNav items={doc.scenes.map((sc, i) => ({ id: sceneDomId(sc, i), label: `Cảnh ${i + 1}` }))} />
        )}
        {!Array.isArray(doc.scenePlan) || doc.scenePlan.length === 0 ? (
          <Alert
            type="warning" showIcon
            message="Kịch bản cũ, chưa có Scene Plan"
            description="Action và các nhịp vẫn được giữ nguyên. UI không tự ghép evidence theo thứ tự cảnh."
          />
        ) : null}
        {hardDefects.length > 0 && (
          <div className="wb-needs" role="region" aria-label="Cảnh báo của kịch bản">
            {hardDefects.map((violation, index) => {
              // Id chip BE theo chỉ số GỐC (đã ghép vào `chipId` ở trên). CRITIC (điểm) nằm ở khối gập và giữ nút F67 ở đó.
              const req = onAskOptions ? warningOptionsRequestOf(episode, violation) : null;
              const label = scenePlanGateLabel(violation.gate) !== violation.gate ? scenePlanGateLabel(violation.gate) : gateLabel(violation.gate);
              return (
                <div className="wb-need" key={`${violation.gate}-${index}`}>
                  <span className="wb-need-t">
                    <Bang tone={violation.severity === 'ERROR' ? 'bad' : 'warn'} />
                    <span><strong>{label}</strong>{violation.message ? <span className="wb-need-d"> — {humanizeCodes(violation.message)}</span> : null}</span>
                  </span>
                  {violation.gate === 'SCENE_METADATA_LOST' && onRestoreMetadata && (
                    <Button size="small" type="primary" ghost loading={restoringMetadata} disabled={!!optionsDisabledReason || restoringMetadata} title={optionsDisabledReason ?? 'Chép địa điểm/thời gian/mục đích từ kế hoạch cảnh — không dùng AI'} onClick={onRestoreMetadata}>
                      Khôi phục (không dùng AI)
                    </Button>
                  )}
                  {req && onAskOptions && <OptionsButton disabledReason={optionsDisabledReason} onClick={() => onAskOptions(req)} />}
                  {onAcceptPlant && canOfferPlantAccept(violation) && (
                    <Button
                      size="small" disabled={!!optionsDisabledReason || acceptingPlant} title={optionsDisabledReason ?? 'Chỉ khi một cảnh của tập ĐÃ có chi tiết này — cần lý do; không sửa kịch bản'}
                      onClick={() => setPlantTarget({ beatId: plantAcceptTargets(violation)[0], message: violation.message })}
                    >
                      Chấp nhận (cảnh đã có chi tiết)…
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        )}
        {softDefects.length > 0 && (
          <Collapse className="wb-soft-warnings" size="small" items={[{
            key: 'soft-warnings', label: `Cảnh báo nhẹ · ${softWarningKindCount(softDefects)} loại — không chặn đẩy`,
            children: <div className="wb-needs">{softDefects.map((v, i) => (
              <div className="wb-need" key={`${v.gate}-${i}`}>
                <span className="wb-need-t"><Bang tone="warn" /><span><strong>{scenePlanGateLabel(v.gate) !== v.gate ? scenePlanGateLabel(v.gate) : gateLabel(v.gate)}</strong>{v.message ? <span className="wb-need-d"> — {humanizeCodes(v.message)}</span> : null}</span></span>
                {onAskOptions && warningOptionsRequestOf(episode, v) && <OptionsButton disabledReason={optionsDisabledReason} onClick={() => onAskOptions(warningOptionsRequestOf(episode, v)!)} />}
              </div>
            ))}</div>,
          }]} />
        )}
        <div data-tour="ep-extras">
          <OtherIssuesBlock rows={otherRows} labelOf={(g) => (scenePlanGateLabel(g) !== g ? scenePlanGateLabel(g) : gateLabel(g))} />
          {/* 261015 P5d (D14): tình tiết nguyên tác chưa lên phim — BE tính lúc đọc ($0 AI); không có mục ⇒ không render. */}
          <DroppedSourceEventsBlock events={rec.droppedSourceEvents} defaultOpen={openDropped} />
        </div>
        {doc.coldOpen && (
          <Alert type="info" message="Mở màn" description={doc.coldOpen} />
        )}
        {doc.scenes.map((sc, i) => {
          const plan = findScenePlanItem(doc.scenePlan, sc.scenePlanId);
          const manual = isManualScene(sc);
          const hasPlan = Array.isArray(doc.scenePlan) && (doc.scenePlan?.length ?? 0) > 0;
          const deliveryMode = plan?.deliveryMode ?? sc.deliveryMode;
          return (
            <section key={sc.scenePlanId ?? i} id={sceneDomId(sc, i)} className="wb-scene">
              <div className="wb-scene-h">
                <span className="wb-scene-head"><span className="wb-pill-s wb-pill-acc">CẢNH {i + 1}</span><span className="wb-scene-title">{`${locName(sc.locationId)}${sc.timeOfDay ? ` · ${sc.timeOfDay}` : ''}`}</span></span>
                <span className="wb-scene-tags">
                  {[deliveryMode && DELIVERY_LABELS[deliveryMode], manual && 'Thêm tay', plan?.origin && ORIGIN_LABELS[plan.origin]].filter(Boolean).join(' · ')}
                </span>
              </div>
              {hasPlan && !plan && !manual && (
                <Alert type="warning" showIcon style={{ marginBottom: 10 }} message="Không tìm thấy metadata của cảnh theo scenePlanId; không tự map theo vị trí." />
              )}
              {plan && <div className="wb-scene-purpose">{plan.purpose}</div>}
              <p className="wb-action">{sc.action}</p>
              {sc.beats.map((b, j) => (
                b.kind === 'dialogue' || b.kind === 'voiceover' ? (
                  <div key={j} className="wb-line" style={characterColorStyle(b.characterId ?? charName(b.characterId))}>
                    <span className="wb-who">{b.kind === 'dialogue' && !b.characterId && b.speakerLabel ? b.speakerLabel : charName(b.characterId)}{b.kind === 'voiceover' ? ' (V.O.)' : ''}</span>
                    {b.cue && <span className="wb-cue">({b.cue})</span>}
                    <span className="wb-say" style={b.kind === 'voiceover' ? { fontStyle: 'italic' } : undefined}>{b.text}</span>
                    {introBeatLabel(b) && <span className="wb-subtext" title="Chữ lấy từ câu giới thiệu đã duyệt ở Tổng quan truyện">{introBeatLabel(b)}</span>}
                    {b.kind === 'dialogue' && b.subtext && <span className="wb-subtext">ngầm: {b.subtext}</span>}
                  </div>
                ) : (
                  <p key={j} className="wb-action">{b.text}</p>
                )
              ))}
              {plan && (
                <details className="wb-plan">
                  <summary>Chi tiết cảnh</summary>
                  <Space direction="vertical" size={4} style={{ width: '100%', marginTop: 6 }}>
                    <Text style={{ fontSize: 13 }}><Text strong>Muốn: </Text>{plan.want}</Text>
                    <Text style={{ fontSize: 13 }}><Text strong>Trở lực: </Text>{plan.obstacle}</Text>
                    <Text style={{ fontSize: 13 }}><Text strong>Bước ngoặt: </Text>{plan.turn}</Text>
                    {plan.adaptationReason && (
                      <Alert type="info" message="Lý do thêm khi chuyển thể" description={plan.adaptationReason} />
                    )}
                    {plan.sourceEvidence.length > 0 ? (
                      <Collapse
                        size="small"
                        items={[{
                          key: 'evidence',
                          label: `Bằng chứng nguồn (${plan.sourceEvidence.length})`,
                          children: (
                            <Space direction="vertical" size={8} style={{ width: '100%' }}>
                              {plan.sourceEvidence.map((evidence, evidenceIndex) => (
                                <div key={`${evidence.chapter}-${evidenceIndex}`}>
                                  <Text type="secondary" style={{ fontSize: 13 }}>Chương {evidence.chapter}</Text>
                                  <Paragraph style={{ margin: '6px 0 0', paddingLeft: 10, borderLeft: '2px solid var(--wb-line)' }}>
                                    “{evidence.quote}”
                                  </Paragraph>
                                </div>
                              ))}
                            </Space>
                          ),
                        }]}
                      />
                    ) : <Alert type="warning" message="Cảnh chưa có bằng chứng nguồn" />}
                  </Space>
                </details>
              )}
            </section>
          );
        })}
        {doc.cliffhanger && (
          <Alert type="info" message="Kết treo" description={doc.cliffhanger} />
        )}
        {onAcceptPlant && (
          <PlantAcceptModal
            target={plantTarget} episode={episode ?? rec.episode} busy={!!acceptingPlant} onCancel={() => setPlantTarget(null)}
            onSubmit={async (beatId, reason) => { try { await onAcceptPlant(beatId, reason); } finally { setPlantTarget(null); } }}
          />
        )}
      </div>
  );
};

export default ScriptReader;
